import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getFaqItems,
  createFaqItem,
  updateFaqItem,
  deleteFaqItem,
} from '../../services/firebaseService';
import toast from 'react-hot-toast';

export default function AdminFaqs() {
  const { t } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ question: '', questionAr: '', answer: '', answerAr: '', order: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFaqs();
  }, []);

  async function fetchFaqs() {
    try {
      const data = await getFaqItems();
      setFaqs(data);
    } catch {
      toast.error(t('admin.failedLoad'));
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(faq) {
    setEditingId(faq.id);
    setForm({
      question: faq.question || '',
      questionAr: faq.questionAr || '',
      answer: faq.answer || '',
      answerAr: faq.answerAr || '',
      order: faq.order || 0,
    });
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setForm({ question: '', questionAr: '', answer: '', answerAr: '', order: 0 });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.question || !form.answer) {
      toast.error(t('admin.questionAnswerRequired'));
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateFaqItem(editingId, form);
        toast.success(t('admin.faqUpdated'));
      } else {
        await createFaqItem(form);
        toast.success(t('admin.faqCreated'));
      }
      handleCancel();
      fetchFaqs();
    } catch {
      toast.error(t('admin.failedSave'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('admin.deleteFaqConfirm'))) return;
    try {
      await deleteFaqItem(id);
      setFaqs(prev => prev.filter(f => f.id !== id));
      toast.success(t('admin.faqDeleted'));
    } catch {
      toast.error(t('admin.failedDelete'));
    }
  }

  if (loading) {
    return <div className="admin-loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="admin-faqs">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.faqManagement')}</h1>
          <p>{t('admin.faqItemsCount', { count: faqs.length })}</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <i className="fas fa-plus"></i> {t('admin.addFaq')}
          </button>
        )}
      </div>

      {showForm && (
        <div className="admin-card admin-faq-form">
          <h3>{editingId ? t('admin.editFaq') : t('admin.newFaq')}</h3>
          <form onSubmit={handleSubmit}>
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>{t('admin.questionEn')} *</label>
                <input
                  type="text"
                  value={form.question}
                  onChange={e => setForm(prev => ({ ...prev, question: e.target.value }))}
                  placeholder={t('admin.enterQuestionEn')}
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>{t('admin.questionAr')}</label>
                <input
                  type="text"
                  value={form.questionAr}
                  onChange={e => setForm(prev => ({ ...prev, questionAr: e.target.value }))}
                  placeholder={t('admin.enterQuestionAr')}
                  dir="rtl"
                />
              </div>
            </div>
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label>{t('admin.answerEn')} *</label>
                <textarea
                  value={form.answer}
                  onChange={e => setForm(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder={t('admin.enterAnswerEn')}
                  rows={4}
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>{t('admin.answerAr')}</label>
                <textarea
                  value={form.answerAr}
                  onChange={e => setForm(prev => ({ ...prev, answerAr: e.target.value }))}
                  placeholder={t('admin.enterAnswerAr')}
                  rows={4}
                  dir="rtl"
                />
              </div>
            </div>
            <div className="admin-form-group" style={{ maxWidth: 200 }}>
              <label>{t('admin.order')}</label>
              <input
                type="number"
                value={form.order}
                onChange={e => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                min={0}
              />
            </div>
            <div className="admin-form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('admin.saving') : editingId ? t('admin.updateFaq') : t('admin.createFaq')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                {t('admin.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {faqs.length === 0 && !showForm ? (
        <div className="admin-empty">
          <i className="fas fa-question-circle"></i>
          <p>{t('admin.noFaqYet')}</p>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">{t('admin.createFirstFaq')}</button>
        </div>
      ) : (
        <div className="admin-faq-list">
          {faqs.map(faq => (
            <div key={faq.id} className="admin-card admin-faq-item">
              <div className="admin-faq-content">
                <div className="admin-faq-order">#{faq.order || 0}</div>
                <div>
                  <strong>{faq.question}</strong>
                  {faq.questionAr && <p className="admin-faq-ar" dir="rtl">{faq.questionAr}</p>}
                  <p className="admin-faq-answer">{faq.answer}</p>
                </div>
              </div>
              <div className="admin-actions">
                <button onClick={() => handleEdit(faq)} className="admin-action-btn edit" title="Edit">
                  <i className="fas fa-edit"></i>
                </button>
                <button onClick={() => handleDelete(faq.id)} className="admin-action-btn delete" title="Delete">
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
